import type {
    DockFlyoutKind,
    DockFlyoutDataModel,
    DockFlyoutGroup,
    DockFlyoutListItem,
    DockFlyoutRowActionId,
    LoadDockFlyoutDataParams,
} from "kci-design-lib";
import { mapActionIdsToActions } from "kci-design-lib";
import { loadAgentsDockFlyoutMockData } from "../mock/agentDockFlyout.mock";
import { getUserChatSessions, getMFRetentionPeriod, type UserChatSessionDTO } from "./chatHistoryApi";
import api from "./api";
import { CONFIG, CHAT_HISTORY_CONFIG } from "../constants";
import { environment } from "../environments/environment";
import { getAccessToken } from '../auth/msalConfig';
export * from "kci-design-lib";

const DEFAULT_OBJECTIVE_SECONDARY_GROUP_ID = "my-objectives";
const DEFAULT_OBJECTIVE_SECONDARY_GROUP_LABEL = "My Objectives";

export const EMPTY_DOCK_FLYOUT_DATA: DockFlyoutDataModel = {
    title: "",
    scopes: [],
    filterSections: [],
    items: [],
    groups: [],
    pagination: {
        page: 1,
        pageSize: 20,
        total: 0,
        hasMore: false,
    },
};

// Separate loader functions for each kind
// Each function can be updated independently to switch from mock to API calls
async function loadAgentsDockFlyoutData(
    params: LoadDockFlyoutDataParams,
): Promise<DockFlyoutDataModel> {
    return loadAgentsDockFlyoutMockData(params);
    // TODO: Replace with API call - e.g., return fetchAgentsDockFlyoutData(params);
}

async function loadWorkspacesDockFlyoutData(
    params: LoadDockFlyoutDataParams,
): Promise<DockFlyoutDataModel> {
    return fetchWorkspacesDockFlyoutData(params);
}

interface WorkspaceDockFlyoutApiResponse {
    title?: string;
    scopes?: Array<{ id?: string; label?: string }>;
    workspaces?: WorkspaceDockFlyoutApiItem[];
    groups?: WorkspaceDockFlyoutApiGroup[];
    count?: number;
    total?: number;
    page?: number;
    pageSize?: number;
    pagination?: {
        page?: number;
        pageSize?: number;
        total?: number;
        hasMore?: boolean;
    };
}

interface WorkspaceAggregatorHost {
    ChatWebApi?: string;
    ChatWebUi?: string;
    WorkspaceApi?: string;
    WorkspaceUi?: string;
}

interface WorkspaceAggregatorConfigItem {
    Name?: string;
    Value?: string;
}

interface WorkspaceAggregatorRegion {
    regionCode?: string;
    RegionCode?: string;
    regionalUiHost?: string;
    RegionalUiHost?: string;
    workspaces?: Array<WorkspaceDockFlyoutApiResponse | WorkspaceDockFlyoutApiItem>;
    Workspaces?: Array<WorkspaceDockFlyoutApiResponse | WorkspaceDockFlyoutApiItem>;
    Hosts?: WorkspaceAggregatorHost;
    Config?: WorkspaceAggregatorConfigItem[];
}

interface WorkspaceDockFlyoutApiItem {
    id?: string;
    workspaceId?: string;
    name?: string;
    title?: string;
    description?: string;
    status?: string;
    docType?: string;
    type?: string;
    userId?: string;
    properties?: {
        engagementId?: string;
        engagementName?: string;
        engagementCountry?: string;
        dataStudioProjectId?: string;
        engagementSiteId?: string;
        environmentFqdn?: string;
        environmentUrl?: string;
    };
    isPinned?: boolean;
    pinnedBy?: string;
    pinnedOn?: string;
    isActive?: boolean;
    isValid?: boolean | null;
    validatedBy?: string | null;
    validatedOn?: string | null;
    validationMessage?: string | null;
    appId?: string;
    pageId?: string;
    unread?: boolean;
    ownership?: string;
    filters?: Record<string, string | string[]>;
    lastUsedAt?: string;
    createdAt?: string;
    navigationMode?: "direct" | "secondary";
    opensSecondary?: boolean;
    regionalUiHost?: string;
    RegionalUiHost?: string;
    regionCode?: string;
    RegionCode?: string;
    Hosts?: WorkspaceAggregatorHost;
    Config?: Record<string, unknown>;
    optionsMenu?: {
        show?: boolean;
        actions?: string[];
    };
    secondaryGroups?: WorkspaceDockFlyoutApiGroup[];
}

interface WorkspaceDockFlyoutApiGroup {
    id?: string;
    label?: string;
    items?: WorkspaceDockFlyoutApiItem[];
}

interface WorkspaceObjectivesApiResponse {
    workspaceId?: string;
    workspace?: Record<string, unknown> | null;
    objectives?: WorkspaceObjectiveListItem[];
    count?: number;
}

export interface WorkspaceSyncCreatedObjective {
    id?: string;
    name?: string;
    processID?: string;
}

export interface WorkspaceSyncResponse {
    workspaceId: string;
    processCount: number;
    eligibleCount: number;
    createdCount: number;
    skippedCount: number;
    createdObjectives: WorkspaceSyncCreatedObjective[];
}

interface WorkspaceObjectiveMilestone {
    id?: string;
    name?: string;
    order?: number;
    dependencies?: string[];
    tasks?: unknown[];
}

interface WorkspaceObjectiveMetadata {
    id?: string;
    name?: string;
    version?: string;
    description?: string;
    tags?: string[];
}

interface WorkspaceObjectiveDetail {
    name?: string;
    description?: string;
    initial_summary?: string;
    Category?: string;
    category?: string;
    objectiveCategory?: string;
    objectivecategory?: string;
    ObjectiveType?: string;
    objectiveType?: string;
    objective_type?: string;
    milestones?: WorkspaceObjectiveMilestone[];
}

interface WorkspaceObjectiveAppSetting {
    Name?: string;
    name?: string;
    Value?: string;
    value?: string;
}

interface WorkspaceObjectiveProperties {
    AppSettings?: WorkspaceObjectiveAppSetting[];
    appSettings?: WorkspaceObjectiveAppSetting[];
}

interface WorkspaceObjectiveListItem {
    id?: string;
    objectiveId?: string;
    name?: string;
    title?: string;
    description?: string;
    status?: string;
    category?: string;
    Category?: string;
    objectiveCategory?: string;
    objectivecategory?: string;
    ObjectiveType?: string;
    objectiveType?: string;
    objective_type?: string;
    Properties?: WorkspaceObjectiveProperties;
    properties?: WorkspaceObjectiveProperties;
    AppSettings?: WorkspaceObjectiveAppSetting[];
    appSettings?: WorkspaceObjectiveAppSetting[];
    metadata?: WorkspaceObjectiveMetadata;
    objective?: WorkspaceObjectiveDetail;
}

function toHostUrl(host: string): string {
    if (/^https?:\/\//i.test(host)) return host;
    return `${/^(localhost|127\.0\.0\.1)(?::\d+)?$/i.test(host) ? "http" : "https"}://${host}`;
}

function getObjectiveExternalUrl(objective: WorkspaceObjectiveListItem): string | undefined {
    const objectiveType = (objective.ObjectiveType ?? objective.objectiveType ?? "").trim().toLowerCase();
    if (objectiveType !== "link") {
        return undefined;
    }

    const appSettings =
        objective.Properties?.AppSettings ??
        objective.Properties?.appSettings ??
        objective.properties?.AppSettings ??
        objective.properties?.appSettings ??
        objective.AppSettings ??
        objective.appSettings ??
        [];
    const externalUrlSetting = appSettings.find((setting) =>
        (setting.Name ?? setting.name ?? "").trim().toUpperCase() === "EXTERNAL_APP_URL",
    );
    const url = externalUrlSetting?.Value ?? externalUrlSetting?.value;

    const normalizedUrl = (url ?? "").trim();
    return /^https?:\/\//i.test(normalizedUrl) ? normalizedUrl : undefined;
}

function isWorkflowObjective(objective: WorkspaceObjectiveListItem): boolean {
    return (objective.ObjectiveType ?? objective.objectiveType ?? "").trim().toLowerCase() === "workflow";
}

function isLinkObjective(objective: WorkspaceObjectiveListItem): boolean {
    return (objective.ObjectiveType ?? objective.objectiveType ?? "").trim().toLowerCase() === "link";
}

function normalizeWorkspaceObjective(
    objective: WorkspaceObjectiveListItem | null | undefined,
): WorkspaceObjectiveListItem | null {
    if (!objective) {
        return null;
    }

    const nestedObjective = objective.objective;
    const resolvedName = objective.name?.trim() || nestedObjective?.name?.trim() || undefined;
    const resolvedDescription =
        objective.description?.trim() ||
        nestedObjective?.description?.trim() ||
        nestedObjective?.initial_summary?.trim() ||
        undefined;
    const resolvedObjectiveType =
        objective.ObjectiveType ??
        objective.objectiveType ??
        objective.objective_type ??
        nestedObjective?.ObjectiveType ??
        nestedObjective?.objectiveType ??
        nestedObjective?.objective_type;
    const resolvedObjectiveCategory =
        objective.Category ??
        objective.category ??
        objective.objectiveCategory ??
        objective.objectivecategory ??
        nestedObjective?.Category ??
        nestedObjective?.category ??
        nestedObjective?.objectiveCategory ??
        nestedObjective?.objectivecategory;

    return {
        ...objective,
        name: resolvedName,
        description: resolvedDescription,
        objectiveType: resolvedObjectiveType,
        objectiveCategory: resolvedObjectiveCategory,
    };
}

function normalizeWorkspaceObjectivesResponse(
    payload: WorkspaceObjectivesApiResponse | null | undefined,
): WorkspaceObjectivesApiResponse {
    if (!payload) {
        return { workspaceId: "", workspace: null, objectives: [], count: 0 };
    }

    const rawObjectives = Array.isArray(payload.objectives) ? payload.objectives : [];
    const objectives = rawObjectives
        .map((objective) => normalizeWorkspaceObjective(objective))
        .filter(Boolean) as WorkspaceObjectiveListItem[];

    return {
        ...payload,
        objectives,
        count: payload.count ?? objectives.length,
    };
}

function resolveWorkspaceGatewayBase(): string {
    const gatewayUrl = environment.APIServiceUrl?.trim();
    if (!gatewayUrl) {
        return window.location.origin;
    }

    if (/^https?:\/\//i.test(gatewayUrl)) {
        return gatewayUrl.replace(/\/+$/, "");
    }

    // Relative settings (e.g. "gatewayworkspaceapi/api/") must be absolutized before `new URL(...)`.
    return new URL(gatewayUrl.replace(/\/+$/, ""), `${window.location.origin}/`).toString();
}

function resolveWorkspaceObjectivesBase(
    item?: Pick<WorkspaceDockFlyoutApiItem, "Hosts" | "Config">,
): string {
    const regionalApiHost = item?.Hosts?.WorkspaceApi as string;
    const protocol = /^(localhost|127\.0\.0\.1)(?::\d+)?(?:\/|$)/i.test(regionalApiHost)
        ? "http"
        : "https";
    const regionalBase = /^https?:\/\//i.test(regionalApiHost)
        ? regionalApiHost.replace(/\/$/, "")
        : `${protocol}://${regionalApiHost.replace(/\/$/, "")}`;

    let gatewayUrl = item?.Config?.AAG_WorkspaceApi as string;
    if (!regionalApiHost) {
        return new URL(resolveWorkspaceGatewayBase(), window.location.origin).toString().replace(/\/$/, "");
    }

    const gatewayPath = gatewayUrl
        ? new URL(gatewayUrl, window.location.origin).pathname.replace(/^\/|\/$/g, "")
        : ""; 
    
    return gatewayPath ? `${regionalBase}/${gatewayPath}` : regionalBase;
}

    function resolveCoreApiBase(): string {
        const baseUrl = environment.apiBaseUrl?.trim();
        if (baseUrl) {
            return baseUrl.replace(/\/$/, "");
        }

        return window.location.origin;
    }

    function readRegionalValue(
        item: WorkspaceDockFlyoutApiItem,
        region: WorkspaceAggregatorRegion,
    ): { Hosts?: WorkspaceAggregatorHost; Config?: Record<string, unknown>; regionalUiHost?: string; regionCode?: string } {
        return {
            Hosts: region.Hosts,
            Config: {
                aadClientId: environment.clientID,
                aadAuthority: environment.authority,
                aadApiScope: environment.apiScope,
                syncfusionLicenseKey: environment.SyncfusionLicenseKey,
                aadRedirectUri: toHostUrl(`${region.Hosts?.WorkspaceUi}/workspace/`),
                ...Object.fromEntries(
                    (region.Config ?? [])
                        .filter((item) => item.Name)
                        .map((item) => [item.Name, item.Value ?? ""]),
                ),
                ...(region.Hosts?.WorkspaceApi
                    ? { WorkspaceApi: toHostUrl(region.Hosts.WorkspaceApi) }
                    : {}),
                ...(region.Hosts?.ChatWebApi
                    ? { ChatWebApi: toHostUrl(region.Hosts.ChatWebApi) }
                    : {}),
            },
            regionalUiHost:
                item.regionalUiHost?.trim() ||
                item.RegionalUiHost?.trim() ||
                region.regionalUiHost?.trim() ||
                region.RegionalUiHost?.trim(),
            regionCode:
                item.regionCode?.trim() ||
                item.RegionCode?.trim() ||
                region.regionCode?.trim() ||
                region.RegionCode?.trim(),
        };
    }

    function normalizeWorkspaceAggregatorResponse(payload: unknown): WorkspaceDockFlyoutApiItem[] {
        if (!Array.isArray(payload)) {
            return [];
        }

        const normalizedItems = payload.flatMap((regionValue): WorkspaceDockFlyoutApiItem[] => {
            if (!regionValue || typeof regionValue !== "object") {
                return [];
            }

            const region = regionValue as WorkspaceAggregatorRegion;
            const combined =
                region.workspaces ??
                region.workspaces ??
                [];

            return combined.flatMap((responseValue): WorkspaceDockFlyoutApiItem[] => {
                if (!responseValue || typeof responseValue !== "object") {
                    return [];
                }

                const response = responseValue as WorkspaceDockFlyoutApiResponse;
                const workspaceItems = Array.isArray(response.workspaces)
                    ? response.workspaces
                    : [responseValue as WorkspaceDockFlyoutApiItem];

                return workspaceItems.map((item) => ({
                    ...item,
                    ...readRegionalValue(item, region),
                    navigationMode: "secondary",
                    opensSecondary: true,
                }));
            });
        });

        const uniqueItems = new Map<string, WorkspaceDockFlyoutApiItem>();
        for (const item of normalizedItems) {
            const workspaceId = String(item.workspaceId ?? item.id ?? "").trim();
            if (workspaceId && !uniqueItems.has(workspaceId)) {
                uniqueItems.set(workspaceId, item);
            }
        }

        return Array.from(uniqueItems.values());
    }

function capitalizeFirstLetter(value: string): string {
    const trimmed = value.trim();
    if (!trimmed) {
        return "";
    }

    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

function readStringFilter(
    filters: Record<string, string | string[]> | undefined,
    key: string,
): string | undefined {
    const value = filters?.[key];
    if (typeof value === "string" && value.trim()) {
        return value.trim();
    }
    return undefined;
}

function buildSecondaryGroupItem(
    item: WorkspaceDockFlyoutApiItem,
    groupId: string,
    parentWorkspaceId: string,
): DockFlyoutListItem | null {
    const id = String(item.id ?? "").trim();
    if (!id) {
        return null;
    }

    const isObjectiveGroup = groupId === "my-objectives";

    return {
        id,
        name: item.name?.trim() || item.title?.trim() || id,
        appId: item.appId?.trim() || CONFIG.WORKSPACE,
        pageId: item.pageId?.trim() || id,
        unread: item.unread === true,
        lastUsedAt: item.lastUsedAt,
        createdAt: item.createdAt,
        navigationMode: "direct",
        opensSecondary: false,
        optionsMenu: {
            show: false,
            actions: [],
        },
        filters: {
            ...(item.filters ?? {}),
            workspaceId: parentWorkspaceId,
            ...(isObjectiveGroup ? { objectiveId: id } : {}),
        },
    };
}

function toWorkspaceListItem(item: WorkspaceDockFlyoutApiItem): DockFlyoutListItem | null {
    const id = String(item.workspaceId ?? item.id ?? "").trim();
    if (!id) {
        return null;
    }

    const validActionIds: DockFlyoutRowActionId[] = ["pin", "share", "edit", "rename", "copy", "delete", "download"];
    const safeActions: DockFlyoutRowActionId[] = Array.isArray(item.optionsMenu?.actions)
        ? item.optionsMenu.actions.filter((action): action is DockFlyoutRowActionId =>
            validActionIds.includes(action as DockFlyoutRowActionId),
        )
        : ["pin"];
    const resolvedActions: DockFlyoutRowActionId[] = safeActions.length > 0 ? safeActions : ["pin"];

    const secondaryGroups = mapWorkspaceSecondaryGroups(
        item.secondaryGroups,
        item.workspaceId?.trim() || readStringFilter(item.filters, "workspaceId") || id,
    );

    return {
        id,
        name: item.name?.trim() || "",
        subtitle: capitalizeFirstLetter(item.description || ""),
        appId: item.appId?.trim() || CONFIG.WORKSPACE,
        pageId: item.pageId?.trim() || CONFIG.FIXED_TAB_HOME,
        unread: item.unread === true,
        pinned: item.isPinned === true,
        lastUsedAt: item.lastUsedAt,
        createdAt: item.createdAt,
        navigationMode: item.navigationMode === "direct" ? "direct" : "secondary",
        opensSecondary: item.opensSecondary ?? item.navigationMode !== "direct",
        optionsMenu: {
            show: item.optionsMenu?.show ?? true,
            actions: mapActionIdsToActions(resolvedActions),
        },
        filters: {
            ...(item.filters ?? {}),
            ...(item.regionalUiHost || item.RegionalUiHost
                ? { regionalUiHost: item.regionalUiHost ?? item.RegionalUiHost ?? "" }
                : {}),
            ...(item.regionCode || item.RegionCode
                ? { regionCode: item.regionCode ?? item.RegionCode ?? "" }
                : {}),
        },
        ...(secondaryGroups ? { secondaryGroups } : {}),
        Hosts: item.Hosts ?? {},
        Config: item.Config ?? {},
    };
}

function mapWorkspaceSecondaryGroups(
    groups: WorkspaceDockFlyoutApiGroup[] | undefined,
    parentWorkspaceId: string,
): DockFlyoutGroup[] | undefined {
    if (!Array.isArray(groups)) {
        return undefined;
    }

    const mapped = groups
        .map((group) => {
            const id = String(group.id ?? "").trim();
            const label = String(group.label ?? "").trim();
            if (!id || !label) {
                return null;
            }

            const items = Array.isArray(group.items)
                ? Array.from(
                    new Map(
                        group.items
                            .map((item) => buildSecondaryGroupItem(item, id, parentWorkspaceId))
                            .filter(Boolean)
                            .map((item) => [item!.id, item!] as const),
                    ).values(),
                )
                : [];

            return {
                id,
                label,
                items,
            };
        })
        .filter(Boolean) as DockFlyoutGroup[];

    const uniqueGroups = new Map<string, DockFlyoutGroup>();
    for (const group of mapped) {
        const existing = uniqueGroups.get(group.id);
        if (!existing) {
            uniqueGroups.set(group.id, group);
            continue;
        }

        const existingItemIds = new Set(existing.items.map((item) => item.id));
        existing.items.push(
            ...group.items.filter((item) => !existingItemIds.has(item.id)),
        );
    }

    return Array.from(uniqueGroups.values());
}

/**
 * Acquires a bearer token and returns it as an Authorization header object.
 * `apiFetch` also acquires a token internally, but only when the request
 * URL's origin is registered in AUTH_PROTECTED_ORIGINS (env-var driven).
 * These workspace endpoints may resolve to an origin that isn't in that set,
 * so we explicitly pass the token to guarantee it is always sent.
 */
async function buildWorkspaceAuthHeaders(): Promise<Record<string, string>> {
    const token = await getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function fetchWorkspacesDockFlyoutData(
    params: LoadDockFlyoutDataParams,
): Promise<DockFlyoutDataModel> {
    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? 20;

    const url = new URL(`${resolveCoreApiBase()}/DockAggregator/WS`);
    const authHeaders = await buildWorkspaceAuthHeaders();

    try {
        const payload = await api.fetch<WorkspaceAggregatorRegion[]>(url.toString(), {
            method: "GET",
            headers: {
                Accept: "application/json",
                ...authHeaders,
            },
        });
        const rawItems = normalizeWorkspaceAggregatorResponse(payload);
        const sortedItems = [...rawItems].sort((a, b) => {
            if (a.isPinned === b.isPinned) {
                return 0;
            }
            return a.isPinned ? -1 : 1;
        });
        const items = sortedItems
            .map(toWorkspaceListItem)
            .filter(Boolean) as DockFlyoutListItem[];
        const resolvedPage = page;
        const resolvedPageSize = pageSize;
        const hasMore = false;

        return {
            title: "Workspaces",
            scopes: [],
            filterSections: [],
            items,
            groups: [],
            pagination: {
                page: resolvedPage,
                pageSize: resolvedPageSize,
                total: rawItems.length,
                hasMore,
            },
        };
    } catch (error) {
        console.warn("[dockFlyoutDataService] Workspace API failed.", error);
        throw error;
       //return   MOCK_WORKSPACE_FLYOUT_DATA;
    }
}

export async function fetchWorkspaceObjectives(
    workspaceId: string,
    item?: WorkspaceDockFlyoutApiItem
): Promise<WorkspaceObjectivesApiResponse> {
    const trimmedWorkspaceId = workspaceId.trim();
    if (!trimmedWorkspaceId) {
        return { workspaceId: "", workspace: null, objectives: [], count: 0 };
    }
    console.log("Fetching workspace objectives for workspaceId:", trimmedWorkspaceId, "with item:", item);
    const baseUrl = resolveWorkspaceObjectivesBase(item);
    const url = new URL(
        `${baseUrl}/workspaces/${encodeURIComponent(trimmedWorkspaceId)}/objectives`
    );
    const authHeaders = await buildWorkspaceAuthHeaders();

    const response = await api.fetch<WorkspaceObjectivesApiResponse>(url.toString(), {
        method: "GET",
        headers: {
            Accept: "application/json",
            ...authHeaders,
        },
    });

    return normalizeWorkspaceObjectivesResponse(response);
}

export async function syncWorkspace(
    workspaceId: string,
    item?: DockFlyoutListItem,
): Promise<WorkspaceSyncResponse> {
    const trimmedWorkspaceId = workspaceId.trim();
    if (!trimmedWorkspaceId) {
        throw new Error("Workspace ID is required to sync objectives.");
    }

    const baseUrl = resolveWorkspaceObjectivesBase(item);
    const url = new URL(`${baseUrl}/workspaces/${encodeURIComponent(trimmedWorkspaceId)}/sync`);
    const authHeaders = await buildWorkspaceAuthHeaders();

    return await api.fetch<WorkspaceSyncResponse>(url.toString(), {
        method: "POST",
        headers: {
            Accept: "application/json",
            ...authHeaders,
        },
    });
}

export function buildSyncedObjectiveSecondaryGroup(
    workspaceId: string,
    objectives: WorkspaceSyncCreatedObjective[] | undefined,
): DockFlyoutGroup {
    const items = Array.isArray(objectives)
        ? objectives.reduce<DockFlyoutListItem[]>((mappedItems, objective) => {
            const objectiveId = String(objective.id ?? "").trim();
            if (!objectiveId) {
                return mappedItems;
            }

            const processID = String(objective.processID ?? "").trim();
            mappedItems.push({
                id: objectiveId,
                name: String(objective.name ?? "").trim() || objectiveId,
                appId: CONFIG.WORKSPACE,
                pageId: objectiveId,
                navigationMode: "direct",
                opensSecondary: false,
                optionsMenu: {
                    show: false,
                    actions: [],
                },
                filters: {
                    workspaceId,
                    objectiveId,
                    objectiveType: "Workflow",
                    objectiveCategory: "ProcessPlanner",
                    ...(processID ? { processID } : {}),
                },
            });

            return mappedItems;
        }, [])
        : [];

    return {
        id: DEFAULT_OBJECTIVE_SECONDARY_GROUP_ID,
        label: DEFAULT_OBJECTIVE_SECONDARY_GROUP_LABEL,
        items,
    };
}

export async function fetchWorkspaceObjectiveDetail(
    workspaceId: string,
    objectiveId: string,
): Promise<Record<string, unknown>> {
    const trimmedWorkspaceId = workspaceId.trim();
    const trimmedObjectiveId = objectiveId.trim();
    if (!trimmedWorkspaceId || !trimmedObjectiveId) {
        return {};
    }

    const baseUrl = resolveWorkspaceGatewayBase();
    const url = new URL(
        `${baseUrl}/workspaces/${encodeURIComponent(trimmedWorkspaceId)}/objectives/${encodeURIComponent(trimmedObjectiveId)}`,
    );
    const authHeaders = await buildWorkspaceAuthHeaders();

    return await api.fetch<Record<string, unknown>>(url.toString(), {
        method: "GET",
        headers: {
            Accept: "application/json",
            ...authHeaders,
        },
    });
}

export function buildObjectiveSecondaryGroup(
    workspaceId: string,
    objectives: WorkspaceObjectivesApiResponse["objectives"],
): DockFlyoutGroup {
    const firstObjective = Array.isArray(objectives) && objectives.length > 0
        ? normalizeWorkspaceObjective(objectives[0])
        : null;

    const groupId = String(firstObjective?.metadata?.id ?? "").trim() ||
        DEFAULT_OBJECTIVE_SECONDARY_GROUP_ID;
    const groupLabel = String(
        firstObjective?.objectiveCategory ??
        firstObjective?.Category ??
        firstObjective?.category ??
        firstObjective?.objectivecategory ??
        firstObjective?.metadata?.name ??
        "",
    ).trim() || DEFAULT_OBJECTIVE_SECONDARY_GROUP_LABEL;

    let items: DockFlyoutListItem[] = [];
    if (Array.isArray(objectives)) {
        items = objectives.reduce<DockFlyoutListItem[]>((mappedItems, objective) => {
            const normalizedObjective = normalizeWorkspaceObjective(objective);
            if (
                !normalizedObjective ||
                (!isWorkflowObjective(normalizedObjective) && !isLinkObjective(normalizedObjective))
            ) {
                return mappedItems;
            }

            const objectiveId = String(
                normalizedObjective.objectiveId ?? normalizedObjective.id ?? "",
            ).trim();
            if (!objectiveId) {
                return mappedItems;
            }

            mappedItems.push({
                id: objectiveId,
                name:
                    normalizedObjective.name?.trim() ||
                    normalizedObjective.title?.trim() ||
                    objectiveId,
                externalUrl: getObjectiveExternalUrl(normalizedObjective),
                appId: CONFIG.WORKSPACE,
                pageId: objectiveId,
                navigationMode: "direct",
                opensSecondary: false,
                optionsMenu: {
                    show: false,
                    actions: [],
                },
                filters: {
                    workspaceId,
                    objectiveId,
                    objectiveType: normalizedObjective.ObjectiveType ?? normalizedObjective.objectiveType ?? '',
                    objectiveCategory: normalizedObjective.objectiveCategory ?? '',
                },
            });

            return mappedItems;
        }, []);
    }

    return {
        id: groupId,
        label: groupLabel,
        items,
    };
}

const CHAT_SCOPES = [
    { id: "all", label: "All" },
    { id: "personal", label: "Personal" },
    { id: "agents", label: "Agents" },
];

const CHAT_ACTIONS = mapActionIdsToActions([
    "pin",
    "rename",
    "download",
    "copy",
    "delete",
]);

function startOfLocalDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Bucket a session using calendar-day boundaries (not rolling 24-hour windows). */
function bucketFor(iso: string): "today" | "week" | "past30" | "past60" | "past90" | "older" {
    const then = new Date(iso);
    if (!Number.isFinite(then.getTime())) return "older";

    const todayStart = startOfLocalDay(new Date());
    const thenStart = startOfLocalDay(then);
    const diffDays = Math.floor(
        (todayStart.getTime() - thenStart.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (diffDays === 0) return "today";
    if (diffDays > 0 && diffDays < CHAT_HISTORY_CONFIG.BUCKET_DAYS_WEEK) return "week";
    if (diffDays > 0 && diffDays < CHAT_HISTORY_CONFIG.BUCKET_DAYS_PAST30) return "past30";
    if (diffDays > 0 && diffDays < CHAT_HISTORY_CONFIG.BUCKET_DAYS_PAST60) return "past60";
    if (diffDays > 0 && diffDays < CHAT_HISTORY_CONFIG.BUCKET_DAYS_PAST90) return "past90";
    return "older";
}

/** Human-friendly "20/08/26 10:06 AM — DataSourceName" subtitle. */
function formatSubtitle(session: UserChatSessionDTO): string {
    const iso =
        session.LastChatDate ||
        session.ModifiedDate ||
        session.CreatedDate;

    const date = new Date(iso);

    const timestamp = date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });

    const source = session.DataSourceName?.trim();

    return source ? `${timestamp} — ${source}` : timestamp;
}

function toListItem(session: UserChatSessionDTO): DockFlyoutListItem {
    const lastUsedAt =
        session.LastChatDate || session.ModifiedDate || session.CreatedDate;
    return {
        id: session.SessionId,
        name: session.Name || "Untitled chat",
        subtitle: formatSubtitle(session),
        appId: CONFIG.CLARA_APP ?? "tru-clara-ai",
        pageId: CONFIG.FIXED_TAB_CLARA_AI_CHAT,
        pinned: session.IsPinned === true,
        unread: false,
        lastUsedAt,
        createdAt: session.CreatedDate,
        navigationMode: "direct",
        optionsMenu: { show: true, actions: CHAT_ACTIONS },
    };
}

function isAgentChatSession(session: UserChatSessionDTO): boolean {
    if ((session.listDataSources?.length ?? 0) > 0) {
        return true;
    }

    const normalizedDataSourceId = String(session.DataSourceId ?? "").trim().toLowerCase();
    if (normalizedDataSourceId && normalizedDataSourceId !== "0" && normalizedDataSourceId !== "null") {
        return true;
    }

    const normalizedLogType = String(session.LogType ?? "").trim().toLowerCase();
    return normalizedLogType === "agent" || normalizedLogType === "agents";
}

function buildChatDockFlyoutModel(
    sessions: UserChatSessionDTO[],
    params: LoadDockFlyoutDataParams,
): DockFlyoutDataModel {
    const query = (params.query ?? "").trim().toLowerCase();
    const selectedScope = String(params.scope ?? "all").toLowerCase();

    let filtered = sessions.filter((session) => session.IsActive !== false);

    if (selectedScope === "agents") {
        filtered = filtered.filter((session) => isAgentChatSession(session));
    } else if (selectedScope === "personal") {
        filtered = filtered.filter((session) => !isAgentChatSession(session));
    }

    if (query) {
        filtered = filtered.filter((session) =>
            (session.Name || "Untitled chat")?.toLowerCase().includes(query),
        );
    }

    const sorted = [...filtered].sort((a, b) => {
        const at = new Date(a.LastChatDate || a.ModifiedDate || a.CreatedDate).getTime();
        const bt = new Date(b.LastChatDate || b.ModifiedDate || b.CreatedDate).getTime();
        return bt - at;
    });

    const items = sorted.map(toListItem);

    const today = sorted.filter((s) =>
        bucketFor(s.LastChatDate || s.ModifiedDate || s.CreatedDate) === "today",
    );
    const week = sorted.filter((s) =>
        bucketFor(s.LastChatDate || s.ModifiedDate || s.CreatedDate) === "week",
    );
    const past30 = sorted.filter((s) =>
        bucketFor(s.LastChatDate || s.ModifiedDate || s.CreatedDate) === "past30",
    );
    const past60 = sorted.filter((s) =>
        bucketFor(s.LastChatDate || s.ModifiedDate || s.CreatedDate) === "past60",
    );
    const past90 = sorted.filter((s) =>
        bucketFor(s.LastChatDate || s.ModifiedDate || s.CreatedDate) === "past90",
    );

    // Sessions older than 90 days: group by calendar month ("August 2026", "July 2026", …)
    const olderSessions = sorted.filter((s) =>
        bucketFor(s.LastChatDate || s.ModifiedDate || s.CreatedDate) === "older",
    );

    // Build an ordered map of monthKey → sessions (newest month first)
    const monthMap = new Map<string, UserChatSessionDTO[]>();
    for (const s of olderSessions) {
        const iso = s.LastChatDate || s.ModifiedDate || s.CreatedDate;
        const date = new Date(iso);
        if (!Number.isFinite(date.getTime())) continue;
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
        if (!monthMap.has(key)) {
            monthMap.set(key, []);
        }
        monthMap.get(key)!.push(s);
    }

    // Sort month keys descending (newest first)
    const sortedMonthKeys = Array.from(monthMap.keys()).sort((a, b) => b.localeCompare(a));

    const monthGroups = sortedMonthKeys.map((key) => {
        const sessions = monthMap.get(key)!;
        const date = new Date(sessions[0].LastChatDate || sessions[0].ModifiedDate || sessions[0].CreatedDate);
        const monthLabel = date.toLocaleDateString(undefined, { month: "long", year: "numeric" });
        return { id: `month-${key}`, label: monthLabel, items: sessions.map(toListItem) };
    });

    const groups = [
        ...(today.length > 0
            ? [{ id: CHAT_HISTORY_CONFIG.GROUP_ID_TODAY, label: CHAT_HISTORY_CONFIG.GROUP_LABEL_TODAY, items: today.map(toListItem) }]
            : []),
        ...(week.length > 0
            ? [{ id: CHAT_HISTORY_CONFIG.GROUP_ID_THIS_WEEK, label: CHAT_HISTORY_CONFIG.GROUP_LABEL_THIS_WEEK, items: week.map(toListItem) }]
            : []),
        ...(past30.length > 0
            ? [{ id: CHAT_HISTORY_CONFIG.GROUP_ID_PAST_30, label: CHAT_HISTORY_CONFIG.GROUP_LABEL_PAST_30, items: past30.map(toListItem) }]
            : []),
        ...(past60.length > 0
            ? [{ id: CHAT_HISTORY_CONFIG.GROUP_ID_PAST_60, label: CHAT_HISTORY_CONFIG.GROUP_LABEL_PAST_60, items: past60.map(toListItem) }]
            : []),
        ...(past90.length > 0
            ? [{ id: CHAT_HISTORY_CONFIG.GROUP_ID_PAST_90, label: CHAT_HISTORY_CONFIG.GROUP_LABEL_PAST_90, items: past90.map(toListItem) }]
            : []),
        ...monthGroups,
    ];

    return {
        title: "Chat History",
        scopes: CHAT_SCOPES,
        filterSections: [],
        items,
        groups,
        pagination: {
            page: params.page ?? 1,
            pageSize: params.pageSize ?? 50,
            total: items.length,
            hasMore: false,
        },
    };
}

/** Convert retention policy unit + interval to a number of days. */
function retentionToDays(interval: number, unit: string): number {
    const u = unit.trim().toLowerCase();
    if (u === 'months') return interval * 30;
    if (u === 'weeks') return interval * 7;
    return interval; // default: treat as days
}

/**
 * Module-level cache so the retention policy API is called exactly once
 * per browser session regardless of how many times the flyout is opened
 * or re-fetches its list.
 *
 * `undefined`  → not yet fetched
 * `null`       → fetched but returned no usable policy (error or Status=false)
 * `RetentionPolicyResponse` → valid cached result
 */
let _cachedRetentionPolicy: import("./chatHistoryApi").RetentionPolicyResponse | null | undefined =
    undefined;

async function loadChatsDockFlyoutData(
    params: LoadDockFlyoutDataParams,
): Promise<DockFlyoutDataModel> {
    // Fetch retention policy only on the very first call; reuse the result
    // for all subsequent calls (scope changes, search queries, etc.).
    if (_cachedRetentionPolicy === undefined) {
        _cachedRetentionPolicy = await getMFRetentionPeriod();
    }

    const sessions = await getUserChatSessions();

    let sessionsToUse = sessions;

    if (
        _cachedRetentionPolicy?.Status &&
        (_cachedRetentionPolicy.RetentionPolicy?.Interval ?? 0) > 0
    ) {
        const retentionDays = retentionToDays(
            _cachedRetentionPolicy.RetentionPolicy.Interval,
            _cachedRetentionPolicy.RetentionPolicy.Unit ?? '',
        );
        const cutoffMs = Date.now() - retentionDays * 24 * 60 * 60 * 1000;

        sessionsToUse = sessions.filter((s) => {
            const dateMs = new Date(
                s.ModifiedDate || s.LastChatDate || s.CreatedDate,
            ).getTime();
            return Number.isFinite(dateMs) && dateMs >= cutoffMs;
        });
    }

    return buildChatDockFlyoutModel(sessionsToUse, params);
}

// Mapping variable for each kind - only this needs to be updated if function names change
const LOADER_BY_KIND: Partial<
    Record<
        DockFlyoutKind,
        (params: LoadDockFlyoutDataParams) => Promise<DockFlyoutDataModel>
    >
> = {
    agents: loadAgentsDockFlyoutData,
    workspaces: loadWorkspacesDockFlyoutData,
    chats: loadChatsDockFlyoutData,
};

export async function loadDockFlyoutData(
    kind: DockFlyoutKind,
    params: LoadDockFlyoutDataParams = {},
): Promise<DockFlyoutDataModel> {
    const loader = LOADER_BY_KIND[kind];

    if (!loader) {
        return EMPTY_DOCK_FLYOUT_DATA;
    }

    return loader(params);
}
